import React, { ReactElement, useState, useEffect } from "react"
import { Button, Loader, Modal, Header } from "semantic-ui-react"
import { useQuery, useMutation } from "@apollo/client"
import { getKeeperDevices } from "services/graphql/queries/keeperDevices"
import { revokeKeeperDevices } from "services/graphql/mutations/keeperDevices"
import {
  KeeperDevicesContainer,
  KeeperDeviceCard,
  KeeperDeviceHeader,
  KeeperDeviceContent,
  DeviceMeta,
  SmallText,
} from "./KeeperDevices.styled"

interface KeeperDevice {
  id: string
  identifier: string
  keeperId: string
  createdAt: string
  status: string
  lastSeenAt?: string
  loginIp?: string
  loginLocation?: string
  platform?: string
  brand?: string
  modelName?: string
  osVersion?: string
  isCurrent?: boolean
}

interface KeeperDevicesProps {
  keeperId: string
}

export default function KeeperDevices({ keeperId }: KeeperDevicesProps): ReactElement {
  const { data, loading, error, refetch } = useQuery(getKeeperDevices, {
    variables: { keeperId },
    fetchPolicy: "network-only",
    skip: !keeperId,
  })

  const [revokeMutation] = useMutation(revokeKeeperDevices)
  // const [selectedDevice, setSelectedDevice] = useState<KeeperDevice | null>(null)
  // const [confirmOpen, setConfirmOpen] = useState(false)
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false)
  const [processing, setProcessing] = useState(false)

  const maskIp = (ip?: string): string => {
    if (!ip) return "-"

    const cleanIp = ip.replace(/^::ffff:/i, "")

    const parts = cleanIp.split(".")

    if (parts.length === 4) {
      return `${parts[0]}.${parts[1]}.xxx.xxx`
    }

    return cleanIp
  }

  useEffect(() => {
    if (keeperId) refetch()
  }, [keeperId])

  if (loading) return <Loader active inline="centered" />
  if (error) return <div>Erreur lors du chargement des appareils</div>

  const currentDevice = data?.getKeeperDevices?.current
  const otherDevices = data?.getKeeperDevices?.others || []

  const devices: KeeperDevice[] = [...(currentDevice ? [currentDevice] : []), ...otherDevices]

  const onRevoke = async () => {
    try {
      setProcessing(true)
      await revokeMutation({ variables: { keeperId } })
      await refetch()
    } catch (e) {
      // ignore for now
    } finally {
      setProcessing(false)
      // setConfirmOpen(false)
      setBulkConfirmOpen(false)
      // setSelectedDevice(null)
    }
  }

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "1em" }}>
        <Button color="red" onClick={() => setBulkConfirmOpen(true)} disabled={devices.length === 0}>
          Retirer l&apos;accès
        </Button>
      </div>

      <KeeperDevicesContainer>
        {devices.map(device => (
          <KeeperDeviceCard key={device.id}>
            <KeeperDeviceHeader>
              <div>
                {device.brand || "Appareil"} {device.modelName || ""}
              </div>
              <SmallText>{device.isCurrent ? "Actuel" : ""}</SmallText>
            </KeeperDeviceHeader>
            <KeeperDeviceContent>
              <DeviceMeta>
                {[device.platform, device.loginLocation, maskIp(device.loginIp)].filter(Boolean).join(" | ") || "-"}
              </DeviceMeta>
              <div style={{ marginTop: 8 }}>Dernière connection :</div>
              <SmallText>{device.lastSeenAt ? new Date(device.lastSeenAt).toLocaleString() : "-"}</SmallText>
            </KeeperDeviceContent>
            {/* <KeeperDeviceFooter>
              <Button
                basic
                color="red"
                onClick={() => {
                  setSelectedDevice(device)
                  setConfirmOpen(true)
                }}
                data-testid={`revoke-${device.id}`}
                size="small"
              >
                Retirer l&apos;appareil
              </Button>
            </KeeperDeviceFooter> */}
          </KeeperDeviceCard>
        ))}
      </KeeperDevicesContainer>
      <Modal open={bulkConfirmOpen} size="small" onClose={() => setBulkConfirmOpen(false)}>
        <Header>Vous êtes sur?</Header>
        <Modal.Content>
          <p>Tous les appareils seront déconnectés immédiatement de ce compte.</p>
        </Modal.Content>
        <Modal.Actions>
          <Button basic color="black" onClick={() => setBulkConfirmOpen(false)}>
            Annuler
          </Button>
          <Button positive onClick={() => onRevoke()} loading={processing}>
            Valider
          </Button>
        </Modal.Actions>
      </Modal>
    </div>
  )
}
